import React from "react";
import { X, Download, FileText, Loader2 } from "lucide-react";
import { PDFViewer, PDFDownloadLink, pdf } from "@react-pdf/renderer";
import { http } from "utils/http/client";
import ReceiptPDF from "../Finance/ReceiptPDF";
import { useAlert } from "components/UI_components/Alert";

export function PaymentReceiptPreviewModal({
    open,
    onClose,
    payment,
    contractor,
    organization
}) {
    const { addAlert } = useAlert();
    // State for the confirmation dialog
    const [isWhatsAppDialogOpen, setIsWhatsAppDialogOpen] = React.useState(false);
    const [whatsAppNumber, setWhatsAppNumber] = React.useState("");
    const [isSending, setIsSending] = React.useState(false);

    // Initialize phone number when modal opens or contractor changes
    React.useEffect(() => {
        if (contractor?.contact) {
            setWhatsAppNumber(contractor.contact);
        }
    }, [contractor, open]);

    // Prepare transaction data for ReceiptPDF
    const transactionData = React.useMemo(() => {
        if (!payment) return null;
        return {
            ...payment,
            contractor_name: contractor?.name,
            contractor: contractor,
            work_order_no: payment.work_order_no || payment.workOrderId,
            payment_mode: payment.payment_mode || payment.mode,
            reference_no: payment.reference_no || payment.reference,
            site_name: payment.site_name || payment.projectName,
            attachment_file: payment.attachment_file
        };
    }, [payment, contractor]);

    // Memoize the PDF document to prevent re-rendering when other state changes
    const PdfDocument = React.useMemo(() => {
        if (!transactionData) return null;
        return <ReceiptPDF transactionData={transactionData} type="contractor" />;
    }, [transactionData]);

    if (!open || !transactionData) return null;

    const handleSendWhatsApp = async () => {
        setIsSending(true);
        try {
            // 1. Generate PDF Blob
            const blob = await pdf(PdfDocument).toBlob();

            // 2. Upload Document
            const formData = new FormData();
            formData.append("type", "document");
            const file = new File([blob], `PaymentReceipt_${transactionData.transaction_id}.pdf`, { type: "application/pdf" });
            formData.append("file", file);

            const uploadResponse = await http("/core/uploadsnagobjects/", {
                method: "POST",
                body: formData
            });

            const uploadData = await uploadResponse.json();
            const documentUrl = uploadData.data?.file || uploadData.file;

            if (!documentUrl) {
                throw new Error("No file URL received from upload");
            }

            // 3. Send WhatsApp Message
            const whatsappPayload = {
                phone_number: whatsAppNumber,
                template_name: "contractor_payment",
                language_code: "en",
                template_params: {
                    document_url: documentUrl,
                    filename: `PaymentReceipt_${transactionData.transaction_id}.pdf`,
                    contractor_name: contractor.name || "Contractor",
                    amount: `${payment.amount?.toLocaleString()}`,
                    workorder_no: payment.work_order_no || payment.workOrderId || "N/A",
                    project_name: payment.site_name || payment.projectName || "Project",
                    workorder_type: payment.type || "N/A",
                    payment_date: new Date(payment.date).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric"
                    }).replace(/\//g, "-"),
                    payment_mode: payment.payment_mode || payment.mode || "N/A",
                    organization: organization?.organization_name || "Organization"
                }
            };

            const whatsappResponse = await fetch("https://whatsapp-svc-funcapp-baefb6fwcgbbc4fq.centralindia-01.azurewebsites.net/api/send_whatsapp_template", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(whatsappPayload)
            });

            if (!whatsappResponse.ok) {
                const errorText = await whatsappResponse.text();
                throw new Error(`WhatsApp API failed: ${errorText}`);
            }

            addAlert("WhatsApp message sent successfully!", "success");
            setIsWhatsAppDialogOpen(false);

        } catch (error) {
            console.error("Error sending WhatsApp:", error);
            addAlert(`Failed to send message: ${error?.message || "Unknown error"}`, "error");
        } finally {
            setIsSending(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 md:p-8 animate-in fade-in duration-200">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200 relative">
                {/* Modal Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gray-50/50">
                    <div className="flex items-center gap-3">
                        <div className="bg-sm-primary/10 p-2 rounded-lg">
                            <FileText className="h-5 w-5 text-sm-primary" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">Payment Receipt Preview</h2>
                            <p className="text-xs text-muted-foreground">Review and share the payment receipt</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setIsWhatsAppDialogOpen(true)}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-[#25D366] text-white rounded-lg text-sm font-medium hover:bg-[#128C7E] transition-all"
                        >
                            <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.008-.57-.008-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                            </svg>
                            WhatsApp
                        </button>
                        <PDFDownloadLink
                            document={PdfDocument}
                            fileName={`PaymentReceipt_${transactionData.transaction_id}.pdf`}
                        >
                            {({ loading }) => (
                                <button
                                    disabled={loading}
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-sm-primary text-white rounded-lg text-sm font-medium hover:bg-sm-primary/90 transition-all disabled:opacity-50"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            Preparing...
                                        </>
                                    ) : (
                                        <>
                                            <Download className="h-4 w-4" />
                                            Download PDF
                                        </>
                                    )}
                                </button>
                            )}
                        </PDFDownloadLink>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                        >
                            <X className="h-5 w-5 text-gray-500" />
                        </button>
                    </div>
                </div>

                {/* Modal Content - PDF Viewer */}
                <div className="flex-1 bg-gray-100 p-2 md:p-4 overflow-hidden relative">
                    <div className="w-full h-full bg-white rounded shadow-sm overflow-hidden">
                        <PDFViewer className="w-full h-full border-none">
                            {PdfDocument}
                        </PDFViewer>
                    </div>
                </div>
            </div>

            {/* WhatsApp Confirmation Dialog Overlay */}
            {isWhatsAppDialogOpen && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-6 space-y-4 animate-in zoom-in-95 duration-200">
                        <h3 className="text-lg font-semibold text-gray-900">Send Receipt</h3>
                        <p className="text-sm text-gray-600">
                            Confirm the WhatsApp number to send the payment receipt to.
                        </p>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">WhatsApp Number</label>
                            <input
                                type="text"
                                value={whatsAppNumber}
                                onChange={(e) => setWhatsAppNumber(e.target.value)}
                                placeholder="+91..."
                                className="w-full h-10 px-3 rounded-md border border-input bg-transparent text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                            />
                            <p className="text-[10px] text-muted-foreground">Make sure to include country code (e.g., +91)</p>
                        </div>
                        <div className="flex justify-end gap-3 pt-2">
                            <button
                                onClick={() => setIsWhatsAppDialogOpen(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                                disabled={isSending}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSendWhatsApp}
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#25D366] rounded-md hover:bg-[#128C7E] disabled:opacity-50"
                                disabled={isSending || !whatsAppNumber}
                            >
                                {isSending ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Sending...
                                    </>
                                ) : (
                                    "Send"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
